import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';

const KEY = 'callLogs';

export function useCallLogs(params = {}) {
  return useQuery({
    queryKey: [KEY, params],
    queryFn: () => api.get('/call-logs', { params }).then((r) => r.data),
    enabled: !!(params.account_id || params.deal_id || params.logged_by || params.follow_up_due || params.q !== undefined),
  });
}

export function useAllCallLogs(params = {}) {
  return useQuery({
    queryKey: [KEY, 'all', params],
    queryFn: () => api.get('/call-logs', { params }).then((r) => r.data),
    staleTime: 2 * 60 * 1000,
  });
}

export function useCallLog(id) {
  return useQuery({
    queryKey: [KEY, id],
    queryFn: () => api.get(`/call-logs/${id}`).then((r) => r.data),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

export function useFollowUps() {
  return useQuery({
    queryKey: [KEY, 'follow-ups'],
    queryFn: () => api.get('/call-logs/follow-ups').then((r) => r.data),
    refetchInterval: 5 * 60 * 1000, // refresh every 5 min
  });
}

export function useCallTemplates() {
  return useQuery({
    queryKey: [KEY, 'templates'],
    queryFn: () => api.get('/call-logs/templates').then((r) => r.data),
    staleTime: 30 * 60 * 1000, // templates rarely change
  });
}

export function useCreateCallLog() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post('/call-logs', body).then((r) => r.data),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: [KEY] });
      qc.invalidateQueries({ queryKey: ['tasks'] });
      qc.invalidateQueries({ queryKey: ['activity', { account_id: vars.account_id }] });
      if (vars.deal_id) qc.invalidateQueries({ queryKey: ['activity', { deal_id: vars.deal_id }] });
    },
  });
}

export function useUpdateCallLog(id) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.patch(`/call-logs/${id}`, body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}

export function useCompleteFollowUp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.post(`/call-logs/${id}/complete-follow-up`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY] });
      qc.invalidateQueries({ queryKey: ['activity'] });
    },
  });
}

export function useDeleteCallLog() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.delete(`/call-logs/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}
